import type { CSSProperties } from 'react';
import type { DivBlock } from '@/types/blocks';

const GAP_CLASS: Record<DivBlock['gap'], string> = { none: 'gap-0', sm: 'gap-2', md: 'gap-4', lg: 'gap-8' };
const PADDING_CLASS: Record<DivBlock['padding'], string> = { none: 'p-0', sm: 'p-3', md: 'p-6', lg: 'p-10' };
const RADIUS_CLASS: Record<DivBlock['radius'], string> = { none: 'rounded-none', sm: 'rounded-lg', lg: 'rounded-2xl', full: 'rounded-[2rem]' };
const JUSTIFY_CLASS: Record<DivBlock['justify'], string> = {
  start: 'justify-start',
  center: 'justify-center',
  end: 'justify-end',
  between: 'justify-between',
};
const ALIGN_CLASS: Record<DivBlock['align'], string> = {
  start: 'items-start',
  center: 'items-center',
  end: 'items-end',
  stretch: 'items-stretch',
};

const SPACING_PX: Record<DivBlock['padding'], number> = { none: 0, sm: 12, md: 24, lg: 40 };

function hasCustomPadding(block: DivBlock): boolean {
  return [block.paddingTop, block.paddingRight, block.paddingBottom, block.paddingLeft].some((value) => value !== undefined);
}

/** Classes Tailwind do container flexível — mesma lógica para admin e site público. */
export function getDivClassName(block: DivBlock): string {
  const direction = block.direction === 'row' ? 'flex-row' : 'flex-col';
  // Linhas são responsivas por definição: os filhos passam para a próxima
  // faixa quando não houver mais espaço disponível no container.
  const wrap = block.direction === 'row' ? 'flex-wrap' : 'flex-nowrap';
  const width = block.contentWidth === 'boxed' ? 'mx-auto w-full max-w-5xl' : 'w-full';
  const padding = hasCustomPadding(block) ? '' : PADDING_CLASS[block.padding];
  return `flex ${direction} ${wrap} ${GAP_CLASS[block.gap]} ${JUSTIFY_CLASS[block.justify]} ${ALIGN_CLASS[block.align]} ${padding} ${RADIUS_CLASS[block.radius]} ${width}`;
}

/** Estilos inline — dependem de valores livres (cor/imagem/altura), não dá para virar classe Tailwind estática. */
export function getDivStyle(block: DivBlock): CSSProperties {
  const fallbackPadding = hasCustomPadding(block) ? SPACING_PX[block.padding] : undefined;
  return {
    width: block.width && block.width > 0 ? `${block.width}px` : undefined,
    height: block.height && block.height > 0 ? `${block.height}px` : undefined,
    minHeight: block.minHeight > 0 ? `${block.minHeight}px` : undefined,
    paddingTop: block.paddingTop ?? fallbackPadding,
    paddingRight: block.paddingRight ?? fallbackPadding,
    paddingBottom: block.paddingBottom ?? fallbackPadding,
    paddingLeft: block.paddingLeft ?? fallbackPadding,
    backgroundColor: block.bgColor || undefined,
    backgroundImage: block.bgImageUrl ? `url(${block.bgImageUrl})` : undefined,
    backgroundSize: block.bgImageUrl ? 'cover' : undefined,
    backgroundPosition: block.bgImageUrl ? 'center' : undefined,
  };
}
